import type { CompetitorOverview } from "@/lib/api/types";
import { competitorNote } from "@/lib/competitors";
import { formatCount } from "@/lib/format";

type List = NonNullable<CompetitorOverview["competitors"]>;

type Props = {
  list: List;
  selected: string[];
  max: number;
  onToggle: (domain: string) => void;
};

const TH = "px-3 py-2 font-medium";
const TD = "px-3 py-2.5 text-[13px]";

/**
 * Wettbewerberliste (ahrefs Organic competitors): Wer rankt für dieselben Keywords wie die eigene Domain?
 * „Nur Wettbewerber“ sind Keywords, für die er rankt und wir nicht, der Hebel der Content-Gap.
 * „Zitate in KI-Antworten“ kommt aus den gespeicherten Trigger-Tests des Prompt-Moduls.
 */
export function CompetitorList({ list, selected, max, onToggle }: Props) {
  const full = selected.length >= max;
  return (
    <div className="overflow-x-auto">
      <table aria-label="Wettbewerber" className="w-full min-w-[760px] border-collapse text-sm">
        <thead>
          <tr className="text-left text-xs text-muted">
            <th className={TH}>
              <span className="sr-only">Für die Content-Gap wählen</span>
            </th>
            <th className={TH}>Domain</th>
            <th className={`${TH} text-right`}>Domain Rating</th>
            <th className={`${TH} text-right`}>Gemeinsame Keywords</th>
            <th className={`${TH} text-right`}>Nur Wettbewerber</th>
            <th className={`${TH} text-right`}>Nur wir</th>
            <th className={`${TH} text-right`}>Zitate in KI-Antworten</th>
          </tr>
        </thead>
        <tbody>
          {list.competitors.map((competitor) => {
            const checked = selected.includes(competitor.domain);
            const note = competitorNote(competitor);
            return (
              <tr key={competitor.domain} className={`border-t border-neutral-bg ${checked ? "bg-[#eef3fa]" : ""}`}>
                <td className="px-3 py-1">
                  <label className="flex h-11 w-11 items-center justify-center">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!checked && full}
                      onChange={() => onToggle(competitor.domain)}
                      aria-label={`${competitor.domain} für die Content-Gap wählen`}
                      className="h-5 w-5"
                    />
                  </label>
                </td>
                <td className={TD}>
                  {competitor.domain}
                  {note && <span className="ml-2 rounded-full bg-neutral-bg px-2 py-0.5 text-xs text-muted">{note}</span>}
                </td>
                <td className={`${TD} text-right font-mono`}>{competitor.domain_rating.toLocaleString("de-DE")}</td>
                <td className={`${TD} text-right font-mono`}>{formatCount(competitor.common)}</td>
                <td className={`${TD} text-right font-mono`}>{formatCount(competitor.only_competitor)}</td>
                <td className={`${TD} text-right font-mono`}>{formatCount(competitor.only_own)}</td>
                <td className={`${TD} text-right font-mono`}>{competitor.ai_runs > 0 ? formatCount(competitor.ai_runs) : "–"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
